import React, { useMemo } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import LogoColorIcon from '@assets/images/logo/svg/logo-color.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconUser from '@assets/images/icons/userPlaceholder.svg'
import { useSelector } from 'react-redux'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  // eslint plugin doesn't recognize animations used in other styles
  // eslint-disable-next-line
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgb(231, 60, 91, 0.7)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(231, 60, 91, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(231, 60, 91, 0)',
    },
  },
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    width: 22,
    height: 22,
    backgroundColor: theme.iconColor,
    color: theme.white,
    fontSize: theme.fontSizes.tooltip,
    lineHeight: theme.fontSizes.tooltip,
    '& > img': {
      borderRadius: '50%',
      border: `1px solid ${theme.borderColors.default}`,
    },
  },
  pointer: {
    cursor: 'pointer',
  },
  demoIcon: {
    '& path': {
      fill: `${theme.white} !important`,
    },
  },
  animated: {
    animationName: '$pulse',
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
  },
  error: {
    background: theme.error,
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    '& path': {
      fill: theme.textSecondary,
    },
  },
}), { name: 'TenantInitialsBubble' })

interface TenantInitialsBubbleProps {
  isDemo?: boolean,
  tenantName: string,
  companyName: string,
  avatarUrl?: string,
  animated?: boolean,
  hasInvites?: boolean,
  displayErrors?: boolean,
  iconColors?: { backgroundColor: string, color: string }
  styles?: { root?: string },
  iconSize?: number,
  minIconSize?: number,
  onClick?: () => void
  showAvatarPlaceholder?: boolean,
}

const defaultProps = {
  tenantName: '',
  companyName: '',
  animated: false,
  hasInvites: false,
  displayErrors: false,
  isDemo: false,
  avatarUrl: null,
  showAvatarPlaceholder: false,
}

const TenantInitialsBubble = (props: TenantInitialsBubbleProps = defaultProps) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const {
    isDemo,
    tenantName,
    companyName,
    avatarUrl,
    animated,
    hasInvites,
    displayErrors,
    styles,
    iconColors = { backgroundColor: theme.primaryBright, color: theme.white },
    iconSize = 16,
    minIconSize,
    showAvatarPlaceholder = false,
    onClick,
  } = props
  const hasConfirmedEmail = useSelector(state => state.user.info.hasConfirmedEmail)
  const hasDanglingSubscriptions = useSelector(state => (state.subscription.details as { hasDanglingSubscriptions?: boolean })?.hasDanglingSubscriptions)

  const initials = useMemo(() => {
    const name = tenantName || companyName || lang.tenant.newTenant
    if (!name) return ''

    return name.split(' ').filter(Boolean).reduce((result, token) => {
      if (result.length < 2) {
        result += token[0]
      }
      return result
    }, '').toUpperCase()
  }, [tenantName, companyName])
  const hasError = displayErrors && (!isDemo && (hasInvites || hasDanglingSubscriptions || !hasConfirmedEmail))
  const isAnimated = hasError && animated
  const iconStyles = {
    backgroundColor: hasError ? theme.error : iconColors.backgroundColor,
    color: iconColors.color,
  }
  const rootClassName = clsx(
    classes.root,
    onClick && classes.pointer,
    isAnimated && classes.animated,
    hasError && classes.error,
    styles?.root && styles.root,
  )

  return (
    <div
      data-qs-id='tenant-initials-bubble'
      style={{
        ...iconStyles,
        width: iconSize,
        height: iconSize,
        ...(minIconSize ? { minWidth: minIconSize, minHeight: minIconSize } : {}),
        fontSize: iconSize * 0.4,
        lineHeight: iconSize * 0.4,
      }}
      className={rootClassName}
      onClick={onClick}
    >
      {
        isDemo
          ? (
            <SVGIcon
              data-qs-id='tenant-initials-bubble-demo-icon'
              svg={LogoColorIcon}
              width={iconSize}
              height={iconSize}
              className={classes.demoIcon} />
          )
          : avatarUrl
            ? <img src={avatarUrl} width={iconSize} height={iconSize} />
            : (showAvatarPlaceholder ? <SVGIcon svg={IconUser} className={classes.avatarPlaceholder} width={iconSize} height={iconSize} /> : initials)
      }
    </div>
  )
}

export default TenantInitialsBubble
